/**
 * Product Selection Modal
 * Allows users to search and select products from the library
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Search, Package, Check, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useProductSearch } from '../hooks/useProductLibrary';
import type { ProductLibraryItem, ProductCategory } from '../types';
import { AIProductCreationModal } from './AIProductCreationModal';

interface ProductSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (product: ProductLibraryItem | null) => void;
  currentProductId?: string;
  category?: ProductCategory;
  subcategory?: string | null;
  title?: string;
  /** When true, shows all products by default instead of filtering by category */
  showAllByDefault?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  fixture: 'Lighting Fixtures',
  control: 'Lighting Controls',
  speaker: 'Speakers',
  access_point: 'Access Points',
  camera: 'Cameras',
  touch_panel: 'Touch Panels',
  amplifier: 'Amplifiers',
  processor: 'Processors',
  display: 'Displays',
  switch: 'Network Switches',
  doorbell: 'Doorbells',
  sensor: 'Sensors',
  microphone: 'Microphones',
  thermostat: 'Thermostats',
  electrical: 'Electrical',
  other: 'Other',
};

const CATEGORY_ALIASES: Record<string, string> = {
  fixture: 'fixture',
  fixtures: 'fixture',
  lighting_fixture: 'fixture',
  lighting_fixtures: 'fixture',
  control: 'control',
  controls: 'control',
  lighting_control: 'control',
  lighting_controls: 'control',
  speaker: 'speaker',
  speakers: 'speaker',
  accesspoint: 'access_point',
  access_points: 'access_point',
  access_point: 'access_point',
  camera: 'camera',
  cameras: 'camera',
  touchpanel: 'touch_panel',
  touch_panels: 'touch_panel',
  touch_panel: 'touch_panel',
  amplifier: 'amplifier',
  amplifiers: 'amplifier',
  processor: 'processor',
  processors: 'processor',
  displays: 'display',
  display: 'display',
  network_switch: 'switch',
  network_switches: 'switch',
  switches: 'switch',
  switch: 'switch',
  doorbells: 'doorbell',
  doorbell: 'doorbell',
  sensors: 'sensor',
  sensor: 'sensor',
  microphones: 'microphone',
  microphone: 'microphone',
  thermostats: 'thermostat',
  thermostat: 'thermostat',
};

function normalizeTaxonomyValue(value?: string | null): string {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function normalizeCategory(value?: string | null): string {
  const normalized = normalizeTaxonomyValue(value);
  return CATEGORY_ALIASES[normalized] || normalized;
}

function getCategoryLabel(category?: string): string {
  if (!category) return 'Products';
  const normalized = normalizeCategory(category);
  return (
    CATEGORY_LABELS[normalized] ||
    category.replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
  );
}

function getRelatedSearchQuery(category?: string): string | undefined {
  const normalizedCategory = normalizeCategory(category);
  if (!normalizedCategory) return undefined;

  return normalizedCategory;
}

function mergeProducts(...productGroups: ProductLibraryItem[][]): ProductLibraryItem[] {
  const seen = new Set<string>();
  const products: ProductLibraryItem[] = [];

  productGroups.forEach(group => {
    group.forEach(product => {
      if (seen.has(product.id)) return;
      seen.add(product.id);
      products.push(product);
    });
  });

  return products;
}

function categoryMatches(productCategory: string | undefined, targetCategory: string | undefined) {
  if (!targetCategory) return true;
  return normalizeCategory(productCategory) === normalizeCategory(targetCategory);
}

function subcategoryMatches(
  productSubcategory: string | undefined,
  targetSubcategory?: string | null,
) {
  if (!targetSubcategory) return false;

  const productValue = normalizeTaxonomyValue(productSubcategory);
  const targetValue = normalizeTaxonomyValue(targetSubcategory);

  if (!productValue || !targetValue) return false;
  return (
    productValue === targetValue ||
    productValue.includes(targetValue) ||
    targetValue.includes(productValue)
  );
}

export function ProductSelectionModal({
  open,
  onOpenChange,
  onSelect,
  currentProductId,
  category,
  subcategory,
  title = 'Select Product',
  showAllByDefault = false,
}: ProductSelectionModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState<'all' | ProductCategory>('all');
  const [showAICreationModal, setShowAICreationModal] = useState(false);
  const initialCategory = showAllByDefault ? undefined : category || 'fixture';
  const initialSubcategory = subcategory;
  const relatedSearchQuery = getRelatedSearchQuery(initialCategory);

  // Search products - don't filter by category if showing all products
  const { data: searchResults, isLoading: isSearching } = useProductSearch({
    query: searchQuery,
    // Don't filter by is_public to include organization-specific products
    limit: 100,
  });

  // Fetch a category-focused result set so related products are not dependent on
  // whichever products happen to be in the first generic page.
  const { data: relatedSearchResults, isLoading: isLoadingRelatedProducts } = useProductSearch({
    query: relatedSearchQuery,
    limit: 250,
    enabled: !!relatedSearchQuery && !showAllByDefault,
  });

  // Get all products as fallback
  const { data: allProducts, isLoading: isLoadingAllProducts } = useProductSearch({
    // Don't filter by is_public to include organization-specific products
    limit: 250,
  });

  const allProductItems = useMemo(() => allProducts?.items || [], [allProducts]);
  const relatedSearchItems = useMemo(
    () => relatedSearchResults?.items || [],
    [relatedSearchResults],
  );
  const candidateProducts = useMemo(
    () => mergeProducts(relatedSearchItems, allProductItems),
    [allProductItems, relatedSearchItems],
  );

  const relatedProducts = useMemo(() => {
    if (!initialCategory || showAllByDefault) return candidateProducts;

    return candidateProducts
      .filter(product => categoryMatches(product.category, initialCategory))
      .sort((a, b) => {
        const aMatchesSubcategory = subcategoryMatches(a.subcategory, initialSubcategory);
        const bMatchesSubcategory = subcategoryMatches(b.subcategory, initialSubcategory);

        if (aMatchesSubcategory === bMatchesSubcategory) {
          return `${a.brand} ${a.model}`.localeCompare(`${b.brand} ${b.model}`);
        }

        return aMatchesSubcategory ? -1 : 1;
      });
  }, [candidateProducts, initialCategory, initialSubcategory, showAllByDefault]);

  const hasRelatedProducts = relatedProducts.length > 0;

  // Determine which products to show
  const products = useMemo(() => {
    if (searchQuery && searchResults) {
      return selectedTab === 'all'
        ? searchResults.items
        : searchResults.items.filter(product => categoryMatches(product.category, selectedTab));
    }
    if (selectedTab === 'all' && allProducts) {
      if (showAllByDefault || !initialCategory) {
        return candidateProducts;
      }

      return hasRelatedProducts ? relatedProducts : candidateProducts;
    }
    return candidateProducts.filter(product => categoryMatches(product.category, selectedTab));
  }, [
    searchQuery,
    searchResults,
    selectedTab,
    allProducts,
    candidateProducts,
    hasRelatedProducts,
    initialCategory,
    relatedProducts,
    showAllByDefault,
  ]);

  const isLoading = isSearching || isLoadingAllProducts || isLoadingRelatedProducts;

  // Set default tab when modal opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedTab('all');
    }
  }, [open]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onOpenChange(false);
      }
    };

    // Use capture phase to ensure we handle it before other handlers
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, onOpenChange]);

  const handleSelectProduct = (product: ProductLibraryItem) => {
    onSelect(product);
    onOpenChange(false);
  };

  const handleKeepGeneric = () => {
    onSelect(null);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-3xl h-[80vh] flex flex-col"
          aria-describedby="product-selection-description"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {title}
            </DialogTitle>
          </DialogHeader>

          <p id="product-selection-description" className="sr-only">
            Select a product from the library to assign to this {initialCategory}
          </p>

          <div className="flex-1 flex flex-col space-y-4 min-h-0">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by brand, model, or description..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                onKeyUp={e => e.stopPropagation()}
                className="pl-10"
              />
            </div>

            {/* Keep Generic Option */}
            <Button variant="outline" className="w-full justify-start" onClick={handleKeepGeneric}>
              <span className="text-muted-foreground">⚡</span>
              <span className="ml-2">Keep Generic (Don't assign specific product)</span>
            </Button>

            {/* Category Tabs - show when showing all products or no products in initial category */}
            {(showAllByDefault || !initialCategory || !hasRelatedProducts) && (
              <Tabs
                value={selectedTab}
                onValueChange={value => setSelectedTab(value as 'all' | ProductCategory)}
              >
                <TabsList className="grid grid-cols-6 w-full">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="fixture">Fixtures</TabsTrigger>
                  <TabsTrigger value="control">Controls</TabsTrigger>
                  <TabsTrigger value="speaker">Speakers</TabsTrigger>
                  <TabsTrigger value="camera">Cameras</TabsTrigger>
                  <TabsTrigger value="other">Other</TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            {/* Product List */}
            <div className="border rounded-lg flex-1 flex flex-col min-h-0">
              <div className="p-2 border-b bg-muted/50 flex-shrink-0">
                <h3 className="text-sm font-medium">
                  {selectedTab === 'all' &&
                  initialCategory &&
                  hasRelatedProducts &&
                  !showAllByDefault
                    ? `Related ${getCategoryLabel(initialCategory)}`
                    : selectedTab === 'all'
                      ? 'All Products'
                      : getCategoryLabel(selectedTab)}
                  {!hasRelatedProducts && selectedTab === 'all' && initialCategory && (
                    <span className="text-xs text-muted-foreground ml-2">
                      (No {getCategoryLabel(initialCategory).toLowerCase()} found)
                    </span>
                  )}
                </h3>
              </div>

              <ScrollArea className="flex-1">
                {isLoading ? (
                  <div className="p-4 space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : products.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    {searchQuery ? 'No products found' : 'No products in this category'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {products.map(product => (
                      <ProductItem
                        key={product.id}
                        product={product}
                        isSelected={product.id === currentProductId}
                        onSelect={() => handleSelectProduct(product)}
                      />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>

          {/* Add New Product Link - Outside the flex container */}
          <div className="text-center pt-4 border-t">
            <Button
              variant="link"
              size="sm"
              className="text-muted-foreground gap-1"
              onClick={() => setShowAICreationModal(true)}
            >
              <Sparkles className="h-3 w-3" />
              Add New Product with AI
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Product Creation Modal */}
      <AIProductCreationModal
        open={showAICreationModal}
        onOpenChange={setShowAICreationModal}
        onSuccess={() => {
          // Close AI modal and refresh the product list
          setShowAICreationModal(false);
          // The product list will auto-refresh via React Query
        }}
      />
    </>
  );
}

interface ProductItemProps {
  product: ProductLibraryItem;
  isSelected: boolean;
  onSelect: () => void;
}

function ProductItem({ product, isSelected, onSelect }: ProductItemProps) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full p-4 text-left hover:bg-accent transition-colors',
        isSelected && 'bg-accent',
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">
              {product.brand} {product.model}
            </h4>
            {isSelected && <Check className="h-4 w-4 text-primary" />}
          </div>

          {product.description && (
            <p className="text-sm text-muted-foreground line-clamp-1">{product.description}</p>
          )}

          <div className="flex items-center gap-4 text-sm">
            {/* Lighting specific info */}
            {product.wattage && <span className="text-muted-foreground">{product.wattage}W</span>}
            {product.lumens && <span className="text-muted-foreground">{product.lumens}lm</span>}
            {product.color_temp && (
              <span className="text-muted-foreground">{product.color_temp}</span>
            )}

            {/* Price */}
            {product.price && <span className="font-medium">${product.price.toFixed(2)}</span>}
          </div>
        </div>

        {/* Verification Badge */}
        {product.is_verified && (
          <Badge variant="secondary" className="ml-2">
            Verified
          </Badge>
        )}
      </div>
    </button>
  );
}
